// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import Input from './Input';
import InvisibleButton from '../buttons/InvisibleButton';

// Utils
import { ENTER_KEY } from '@/utils/constants';
import { Tooltip } from '../tooltips';

type Props = {
  value?: string | number;
  onValueChange?: (value: string) => void;
  dark?: boolean;
  className?: string;
  placeholder?: string;
  compact?: boolean;
  defaultToEdit?: boolean;
  readOnly?: boolean;
  onBlur: () => void;
  type?: 'number' | 'text';
  formattedValue?: string;
  tooltipConfig?: {
    show: boolean;
    message: string;
  };
};

type State = {
  editing: boolean;
};

const INPUT_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
};

class EditableInput extends React.Component<Props, State> {
  private inputRef = React.createRef<Input>();

  static defaultProps = {
    onBlur: () => {},
    type: INPUT_TYPES.TEXT,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      editing: this.props.defaultToEdit !== undefined ? this.props.defaultToEdit : !this.props.value,
    };
  }

  onFocus = () => {
    if (this.props.readOnly) {
      return;
    }

    this.setState({ editing: true }, () => {
      if (this.inputRef.current) {
        this.inputRef.current.selectAll();
      }
    });
  };

  onBlur = () => {
    const { readOnly, value, type } = this.props;

    if (readOnly || (type === INPUT_TYPES.TEXT && !value)) {
      return;
    }

    this.setState({ editing: false });
    this.props.onBlur();
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ENTER_KEY) {
      this.onBlur();
    }
  };

  renderInput = () => {
    const { type, dark, compact, value, onValueChange, placeholder } = this.props;

    return (
      <div className="value-input" onBlur={this.onBlur} onKeyDown={this.handleKeyDown}>
        <Input
          dark={dark}
          compact={compact}
          ref={this.inputRef}
          defaultValue={typeof value === 'string' ? value : undefined}
          value={typeof value === 'number' ? value : undefined}
          onValueChange={onValueChange}
          placeholder={placeholder}
          type={type}
        />
      </div>
    );
  };

  renderEditIcon = () => {
    const { tooltipConfig } = this.props;
    const show = tooltipConfig?.show;
    const message = tooltipConfig?.message;

    if (!show) {
      return <i className="icon-pencil" />;
    }
    return (
      <Tooltip className="custom-tooltip-v2" message={message}>
        <i className="icon-pencil" />
      </Tooltip>
    );
  };

  render() {
    const { editing } = this.state;
    const { value, formattedValue, className, readOnly, dark, placeholder, onValueChange, compact, defaultToEdit, ...rest } = this.props;

    const isInEdit = !readOnly && editing;

    const classes = classNames('editable-input', className, {
      dark,
      'read-only': readOnly,
      editing: isInEdit,
    });

    return (
      <div className={classes} {...rest}>
        <div className="editable-input-container d-flex align-items-center">
          {!isInEdit && (
            <>
              {!readOnly ? (
                <InvisibleButton onClick={this.onFocus} onFocus={this.onFocus} ariaLabel={`Edit ${placeholder || 'value'}`} className="displayed-input">
                  <span className="displayed-value">{formattedValue || value || placeholder}</span>
                  {this.renderEditIcon()}
                </InvisibleButton>
              ) : (
                <div className="displayed-input">
                  <span className="displayed-value">{formattedValue || value || placeholder}</span>
                </div>
              )}
            </>
          )}

          {isInEdit && this.renderInput()}
        </div>
      </div>
    );
  }
}

export default EditableInput;
